import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogField, DialogFooter, dialogInputStyle } from "@/components/ui/Dialog";
import { useUpdateWorkspace } from "@/hooks/useUpdateWorkspace";
import { ApiError } from "@/lib/api";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: UUID;
  initialName: string;
}

export function RenameWorkspaceDialog({ open, onOpenChange, workspaceId, initialName }: Props) {
  const nameId = useId();
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const mutation = useUpdateWorkspace();

  useEffect(() => {
    if (open) {
      setName(initialName);
      setError(null);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && trimmed !== initialName && !mutation.isPending;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      await mutation.mutateAsync({ workspaceId, payload: { name: trimmed } });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t.workspaceSlugTaken);
      } else {
        setError(err instanceof Error ? err.message : t.unknownError);
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t.renameWorkspaceTitle}
      description={t.renameWorkspaceDescription}
    >
      <form onSubmit={onSubmit}>
        <DialogField label={t.workspaceName} htmlFor={nameId} error={error ?? undefined}>
          <input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={255}
            style={dialogInputStyle}
            data-testid="rename-workspace-name"
          />
        </DialogField>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t.cancel}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={!canSubmit}
            data-testid="rename-workspace-submit"
          >
            {mutation.isPending ? t.saving : t.save}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
